import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, CheckCircle2, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import { useCategories } from "@/context/CategoriesContext";
import { LOCATIONS } from "@/lib/categories";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  category: "Сигнали",
  location: LOCATIONS[0],
  description: "",
  image_url: "",
};

export default function SubmitSignal() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const { categories } = useCategories();

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.location || !form.description.trim()) {
      toast.error("Моля попълнете име, локация и описание.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/signals", form);
      setDone(true);
      toast.success("Сигналът е изпратен успешно!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Layout showTicker={false}>
        <div className="mx-auto max-w-lg px-4 py-24 text-center" data-testid="signal-success">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <CheckCircle2 className="h-9 w-9 text-green-600" />
          </div>
          <h1 className="mt-6 font-display text-3xl font-extrabold text-slate-900">
            Благодарим Ви!
          </h1>
          <p className="mt-3 text-slate-600">
            Вашият сигнал беше получен от редакцията на „Подуяне Днес“. Ще го
            проверим и, ако е необходимо, ще предприемем действия.
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <button
              onClick={() => {
                setForm(EMPTY);
                setDone(false);
              }}
              data-testid="signal-new-btn"
              className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:border-primary hover:text-primary"
            >
              Нов сигнал
            </button>
            <button
              onClick={() => navigate("/")}
              className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0041A8]"
            >
              Към началото
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const inputClass =
    "w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/20";
  const labelClass = "mb-1.5 block text-sm font-semibold text-slate-700";

  return (
    <Layout showTicker={false}>
      <Seo
        title="Изпрати сигнал"
        description="Забелязахте проблем в квартал Подуяне? Подайте граждански сигнал до редакцията на „Подуяне Днес“."
      />
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">
            Гражданска журналистика
          </span>
          <h1 className="mt-4 font-display text-3xl font-extrabold text-slate-900 sm:text-4xl">
            Изпрати сигнал
          </h1>
          <p className="mx-auto mt-3 max-w-md text-slate-600">
            Забелязахте проблем в квартала? Споделете го с нас — заедно правим
            Подуяне по-добро място.
          </p>
        </div>

        <div className="mt-6 flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Сигналите се преглеждат от редакцията. Не публикувайте лични данни на
            трети лица.
          </p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="signal-form">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Вашето име *</label>
              <input
                className={inputClass}
                data-testid="signal-name"
                value={form.name}
                onChange={set("name")}
                placeholder="Име и фамилия"
              />
            </div>
            <div>
              <label className={labelClass}>Телефон</label>
              <input
                className={inputClass}
                data-testid="signal-phone"
                value={form.phone}
                onChange={set("phone")}
                placeholder="Незадължително"
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Имейл</label>
            <input
              type="email"
              className={inputClass}
              data-testid="signal-email"
              value={form.email}
              onChange={set("email")}
              placeholder="Незадължително — за обратна връзка"
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Тип сигнал *</label>
              <select
                className={inputClass}
                data-testid="signal-category"
                value={form.category}
                onChange={set("category")}
              >
                {categories.map((c) => (
                  <option key={c.id || c.slug} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Локация *</label>
              <select
                className={inputClass}
                data-testid="signal-location"
                value={form.location}
                onChange={set("location")}
              >
                {LOCATIONS.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>Описание *</label>
            <textarea
              rows={5}
              className={inputClass}
              data-testid="signal-description"
              value={form.description}
              onChange={set("description")}
              placeholder="Опишете проблема възможно най-подробно — какво, къде и от кога."
            />
          </div>

          <div>
            <label className={labelClass}>Връзка към снимка (URL)</label>
            <input
              className={inputClass}
              data-testid="signal-image"
              value={form.image_url}
              onChange={set("image_url")}
              placeholder="https://... (незадължително)"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="signal-submit-btn"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#0041A8] disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
            Изпрати сигнал
          </button>
        </form>
      </div>
    </Layout>
  );
}
